// src/components/quiz/quizGenerators.js
// I generatori di domande a scelta multipla per FantaQuizz, basati sui dati
// reali dei giocatori già sincronizzati (players collection). Usati sia dalla
// modalità Sopravvivenza sia da Time Attack.
import { shuffle, fmtNum } from './quizShared';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION } from '../auction/MantraRoleConfiguration';
import { buildRoleOverrideMap, normalizeNameText, splitRoleTokens, getMantraBucket } from '../auction/auctionRosterUtils';

// Mappa nome→ruoli Mantra (es. "Ds/Dc/E"), stessa fonte usata da Simula rosa e
// dai tab asta. Costruita una sola volta.
let _mantraRoleMap = null;
function getMantraRoleMap() {
  if (!_mantraRoleMap) {
    _mantraRoleMap = buildRoleOverrideMap({ roleOverrides: DEFAULT_MANTRA_ROLE_CONFIGURATION });
  }
  return _mantraRoleMap;
}

export function minCapsThreshold(players) {
  const maxCaps = Math.max(...players.map(p => Number(p.caps || 0)));
  return Math.ceil(maxCaps * 0.2);
}

function genBestStat(players, statKey, questionText, dec = 0, gkOnly = false) {
  const minCaps = minCapsThreshold(players);
  const eligible = players
    .filter(p => {
      if (gkOnly && String(p.role || '').trim().toUpperCase() !== 'P') return false;
      if (Number(p.caps || 0) < minCaps) return false;
      return Number(p[statKey] || 0) > 0;
    })
    .sort((a, b) => Number(b[statKey] || 0) - Number(a[statKey] || 0));

  if (eligible.length < 4) return null;

  // Pool più ampio (metà degli idonei, non solo il 35% di testa) così il
  // "vincitore" varia di più da una domanda all'altra invece di ricadere
  // quasi sempre sugli stessi 3-4 nomi in cima alla classifica assoluta.
  const topPool  = eligible.slice(0, Math.max(5, Math.floor(eligible.length * 0.5)));
  const winner   = topPool[Math.floor(Math.random() * topPool.length)];
  const winVal   = Number(winner[statKey] || 0);
  const winRole  = String(winner.role || '').trim().toUpperCase();

  // Distractors: stesso ruolo del vincitore, stat < vincitore
  let distractors = eligible.filter(
    p => p.id !== winner.id &&
         String(p.role || '').trim().toUpperCase() === winRole &&
         Number(p[statKey] || 0) < winVal &&
         Number(p[statKey] || 0) >= winVal * 0.45
  );
  if (distractors.length < 3) {
    distractors = eligible.filter(
      p => p.id !== winner.id &&
           String(p.role || '').trim().toUpperCase() === winRole &&
           Number(p[statKey] || 0) < winVal
    );
  }
  // Fallback senza filtro ruolo (non dovrebbe servire con dataset reali)
  if (distractors.length < 3) {
    distractors = eligible.filter(p => p.id !== winner.id && Number(p[statKey] || 0) < winVal);
  }
  if (distractors.length < 3) return null;

  const chosen  = shuffle(distractors).slice(0, 3);
  const options = shuffle([winner, ...chosen]);

  // Tutte le opzioni che hanno lo stesso valore massimo sono corrette
  const maxInOptions = Math.max(...options.map(p => Number(p[statKey] || 0)));
  const correctIndices = options
    .map((p, i) => (Number(p[statKey] || 0) === maxInOptions ? i : -1))
    .filter(i => i !== -1);

  const tiedNames = correctIndices.map(i => options[i].name).join(' / ');
  const explanation =
    correctIndices.length > 1
      ? `${tiedNames} — tutti con ${fmtNum(maxInOptions, dec)}`
      : `${options[correctIndices[0]].name} — ${fmtNum(maxInOptions, dec)} (${options[correctIndices[0]].team || '—'})`;

  return {
    question: questionText,
    options: options.map(p => ({ label: p.name, sub: p.team || '', id: p.id, statValue: fmtNum(p[statKey] || 0, dec) })),
    correctIndices,
    explanation,
  };
}

function genWhichTeam(players) {
  const minCaps = minCapsThreshold(players);
  const eligible = players.filter(p => p.name && p.team && Number(p.caps || 0) >= minCaps);
  if (eligible.length < 8) return null;

  const player   = eligible[Math.floor(Math.random() * eligible.length)];
  const allTeams = [...new Set(eligible.map(p => p.team).filter(Boolean))];
  const others   = shuffle(allTeams.filter(t => t !== player.team)).slice(0, 3);
  if (others.length < 3) return null;

  const options        = shuffle([player.team, ...others]);
  const correctIndices = [options.indexOf(player.team)];

  return {
    question: `In che squadra gioca ${player.name}?`,
    options: options.map(t => ({ label: t, sub: '', id: t })),
    correctIndices,
    explanation: `${player.name} gioca nel ${player.team}`,
  };
}

function genWhoScoredX(players) {
  const minCaps = minCapsThreshold(players);
  const scorers = players.filter(
    p => Number(p.goals || 0) >= 3 &&
         Number(p.caps || 0) >= minCaps &&
         String(p.role || '').trim().toUpperCase() !== 'P'
  );
  if (scorers.length < 4) return null;

  const correct  = scorers[Math.floor(Math.random() * scorers.length)];
  const xGoals   = Number(correct.goals);
  const others   = shuffle(scorers.filter(p => p.id !== correct.id && Number(p.goals || 0) !== xGoals)).slice(0, 3);
  if (others.length < 3) return null;

  const options        = shuffle([correct, ...others]);
  // tutti i giocatori col valore esatto xGoals sono corretti
  const correctIndices = options.map((p, i) => Number(p.goals || 0) === xGoals ? i : -1).filter(i => i !== -1);

  return {
    question: `Chi ha segnato esattamente ${xGoals} gol in questa stagione?`,
    options: options.map(p => ({ label: p.name, sub: p.team || '', id: p.id, statValue: fmtNum(p.goals || 0) })),
    correctIndices,
    explanation: `${correct.name} — ${xGoals} gol (${correct.team || '—'})`,
    scoreMultiplier: 1.5,
  };
}

function genWhoConcededX(players) {
  const minCaps = minCapsThreshold(players);
  const gks = players.filter(
    p => String(p.role || '').trim().toUpperCase() === 'P' &&
         Number(p.caps || 0) >= minCaps &&
         Number(p.gotgoals || 0) > 0
  );
  if (gks.length < 4) return null;

  const correct  = gks[Math.floor(Math.random() * gks.length)];
  const xGoals   = Number(correct.gotgoals || 0);
  const others   = shuffle(gks.filter(p => p.id !== correct.id && Number(p.gotgoals || 0) !== xGoals)).slice(0, 3);
  if (others.length < 3) return null;

  const options        = shuffle([correct, ...others]);
  const correctIndices = options.map((p, i) => Number(p.gotgoals || 0) === xGoals ? i : -1).filter(i => i !== -1);

  return {
    question: `Quale portiere ha subito esattamente ${xGoals} gol in questa stagione?`,
    options: options.map(p => ({ label: p.name, sub: p.team || '', id: p.id, statValue: fmtNum(p.gotgoals || 0) })),
    correctIndices,
    explanation: `${correct.name} — ${xGoals} gol subiti (${correct.team || '—'})`,
    scoreMultiplier: 1.5,
  };
}

function genHowManyStat(players, statKey, questionFn, dec = 0, minVal = 1, excludeRole = null) {
  const minCaps = minCapsThreshold(players);
  const eligible = players.filter(p => {
    if (excludeRole && String(p.role || '').trim().toUpperCase() === excludeRole) return false;
    if (Number(p.caps || 0) < minCaps) return false;
    return Number(p[statKey] || 0) >= minVal && p.name;
  });
  if (eligible.length < 4) return null;

  const correct = eligible[Math.floor(Math.random() * eligible.length)];
  const correctVal = Number(correct[statKey] || 0);

  // Genera 3 valori numerici diversi plausibili vicini al valore reale
  const allVals = [...new Set(eligible.map(p => Number(p[statKey] || 0)).filter(v => v !== correctVal))];
  if (allVals.length < 3) return null;
  const distractors = shuffle(allVals).slice(0, 3);

  const options = shuffle([correctVal, ...distractors]);
  const correctIndices = [options.indexOf(correctVal)];

  return {
    question: questionFn(correct),
    options: options.map(v => ({ label: fmtNum(v, dec), sub: '', id: String(v) })),
    correctIndices,
    explanation: `${correct.name} — ${fmtNum(correctVal, dec)} (${correct.team || '—'})`,
    scoreMultiplier: 1.5,
  };
}

function genNotInTeam(players) {
  const minCaps = minCapsThreshold(players);
  const eligible = players.filter(p => p.name && p.team && Number(p.caps || 0) >= minCaps);
  const teams = [...new Set(eligible.map(p => p.team).filter(Boolean))];
  if (teams.length < 2) return null;

  const team = teams[Math.floor(Math.random() * teams.length)];
  const inTeam = shuffle(eligible.filter(p => p.team === team)).slice(0, 3);
  const outsiders = eligible.filter(p => p.team !== team);
  if (inTeam.length < 3 || outsiders.length < 1) return null;

  const outsider = outsiders[Math.floor(Math.random() * outsiders.length)];
  const options = shuffle([...inTeam, outsider]);
  const correctIndices = [options.indexOf(outsider)];

  return {
    question: `Quale di questi giocatori NON gioca nel ${team}?`,
    options: options.map(p => ({ label: p.name, sub: '', id: p.id })),
    correctIndices,
    explanation: `${outsider.name} gioca nel ${outsider.team || '—'}, non nel ${team}`,
  };
}

function genBestCombined(players) {
  const minCaps = minCapsThreshold(players);
  const eligible = players
    .filter(p => {
      const role = String(p.role || '').trim().toUpperCase();
      return (role === 'A' || role === 'C') &&
             Number(p.caps || 0) >= minCaps &&
             (Number(p.goals || 0) + Number(p.assists || 0)) > 0;
    })
    .map(p => ({ ...p, combined: Number(p.goals || 0) + Number(p.assists || 0) }))
    .sort((a, b) => b.combined - a.combined);

  if (eligible.length < 4) return null;

  const topPool = eligible.slice(0, Math.max(5, Math.floor(eligible.length * 0.5)));
  const winner  = topPool[Math.floor(Math.random() * topPool.length)];
  const winVal  = winner.combined;

  let distractors = shuffle(eligible.filter(p => p.id !== winner.id && p.combined < winVal)).slice(0, 3);
  if (distractors.length < 3) return null;

  const options = shuffle([winner, ...distractors]);
  const maxInOptions = Math.max(...options.map(p => p.combined));
  const correctIndices = options.map((p, i) => p.combined === maxInOptions ? i : -1).filter(i => i !== -1);

  return {
    question: 'Chi ha contribuito di più (gol + assist) tra questi calciatori?',
    options: options.map(p => ({ label: p.name, sub: p.team || '', id: p.id, statValue: String(p.combined) })),
    correctIndices,
    explanation: `${options[correctIndices[0]].name} — ${maxInOptions} contributi (${options[correctIndices[0]].team || '—'})`,
  };
}

// Ruolo classic in chiaro, per genWhichRole: unica domanda del pacchetto che
// non è una classifica statistica ("chi ha fatto più X"), aggiunta apposta
// per variare anche il TIPO di domanda, non solo il giocatore coinvolto.
const ROLE_NAMES = { P: 'Portiere', D: 'Difensore', C: 'Centrocampista', A: 'Attaccante' };

function genWhichRole(players) {
  const minCaps = minCapsThreshold(players);
  const eligible = players.filter(
    p => p.name && p.team &&
         ROLE_NAMES[String(p.role || '').trim().toUpperCase()] &&
         Number(p.caps || 0) >= minCaps
  );
  if (eligible.length < 4) return null;

  const player = eligible[Math.floor(Math.random() * eligible.length)];
  const correctRole = String(player.role || '').trim().toUpperCase();
  const otherRoles  = Object.keys(ROLE_NAMES).filter(r => r !== correctRole);

  const options        = shuffle([correctRole, ...otherRoles]);
  const correctIndices = [options.indexOf(correctRole)];

  return {
    question: `Che ruolo gioca ${player.name}?`,
    options: options.map(r => ({ label: ROLE_NAMES[r], sub: '', id: r })),
    correctIndices,
    explanation: `${player.name} gioca come ${ROLE_NAMES[correctRole]} (${player.team || '—'})`,
  };
}

// "Che ruolo Mantra ha X?" — 4 combinazioni di ruoli Mantra, quella giusta è
// quella reale del giocatore. I distrattori sono combo reali di ALTRI giocatori
// (così sono plausibili), preferendo quelli dello stesso reparto per non
// rendere la domanda banale.
function genMantraRole(players) {
  const map = getMantraRoleMap();
  const minCaps = minCapsThreshold(players);

  const eligible = players
    .map((p) => {
      const combo = map[normalizeNameText(p.name || '')];
      return combo ? { player: p, tokens: splitRoleTokens(combo) } : null;
    })
    .filter(Boolean)
    .filter(({ player, tokens }) =>
      player.name &&
      tokens.length > 0 &&
      Number(player.caps || 0) >= minCaps &&
      !(tokens.length === 1 && tokens[0] === 'P') // niente portieri puri: risposta scontata
    );

  if (eligible.length < 8) return null;

  const comboKey = (tokens) => [...tokens].sort().join('|');
  const fmtCombo = (tokens) => tokens.join(', ');

  const winner = eligible[Math.floor(Math.random() * eligible.length)];
  const correctKey = comboKey(winner.tokens);
  const winnerBucket = getMantraBucket(winner.tokens.join('/'));

  const pool = [];
  const seen = new Set([correctKey]);
  const addFrom = (list, sameBucketOnly) => {
    for (const e of list) {
      if (pool.length >= 3) return;
      const k = comboKey(e.tokens);
      if (seen.has(k)) continue;
      if (sameBucketOnly && getMantraBucket(e.tokens.join('/')) !== winnerBucket) continue;
      seen.add(k);
      pool.push(e.tokens);
    }
  };
  const shuffled = shuffle(eligible);
  addFrom(shuffled, true);
  addFrom(shuffled, false);
  if (pool.length < 3) return null;

  const optionTokens = shuffle([winner.tokens, ...pool]);
  const correctIndices = optionTokens
    .map((tk, i) => (comboKey(tk) === correctKey ? i : -1))
    .filter((i) => i !== -1);

  return {
    question: `Che ruolo Mantra ha ${winner.player.name}?`,
    options: optionTokens.map((tk) => ({ label: fmtCombo(tk), roleTokens: tk, sub: '', id: comboKey(tk) })),
    correctIndices,
    explanation: `${winner.player.name} nel Mantra è ${fmtCombo(winner.tokens)}${winner.player.team ? ` (${winner.player.team})` : ''}`,
  };
}

const GENERATORS = [
  p => genBestStat(p, 'goals',      'Chi ha fatto più gol tra questi calciatori?'),
  p => genBestStat(p, 'assists',    'Chi ha fatto più assist tra questi calciatori?'),
  p => genBestStat(p, 'caps',       'Chi ha giocato più partite tra questi calciatori?'),
  p => genBestStat(p, 'fmarkavg',   'Chi ha la media fanta voto più alta tra questi calciatori?', 2),
  p => genBestStat(p, 'markavg',    'Chi ha la media voto più alta tra questi calciatori?', 2),
  p => genBestStat(p, 'ycards',     'Chi ha preso più cartellini gialli tra questi calciatori?'),
  p => genBestStat(p, 'rcards',     'Chi ha preso più cartellini rossi tra questi calciatori?'),
  p => genBestStat(p, 'penalties',  'Chi ha segnato più rigori tra questi calciatori?'),
  p => genBestStat(p, 'mpenalties', 'Chi ha sbagliato più rigori tra questi calciatori?'),
  p => genBestStat(p, 'owngoals',   'Chi ha segnato più autogol tra questi calciatori?'),
  p => genWhichTeam(p),
  p => genWhichRole(p),
  p => genMantraRole(p),
  p => genWhoScoredX(p),
  p => genWhoConcededX(p),
  p => genBestStat(p, 'spenalties','Chi ha parato più rigori tra questi portieri?', 0, true),
  p => genHowManyStat(p, 'goals',   c => `Quanti gol ha segnato ${c.name} in questa stagione?`, 0, 3, 'P'),
  p => genHowManyStat(p, 'assists', c => `Quanti assist ha fatto ${c.name} in questa stagione?`, 0, 2, 'P'),
  p => genNotInTeam(p),
  p => genBestCombined(p),
];

// Genera una singola domanda, evitando (quando possibile) di ripetere
// il testo di una delle domande recenti (per non far apparire due volte
// di fila la stessa domanda in una partita senza limite di round).
export function pickNextQuestion(players, recentTexts = []) {
  const pool = shuffle(GENERATORS);
  const attempts = pool.length * 4;

  for (let i = 0; i < attempts; i++) {
    const q = pool[i % pool.length](players);
    if (q && !recentTexts.includes(q.question)) return q;
  }
  // Fallback: accetta anche una domanda già vista di recente
  for (let i = 0; i < attempts; i++) {
    const q = pool[i % pool.length](players);
    if (q) return q;
  }
  return null;
}
